import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  type SimpleChanges,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { CompanyService } from '@app/core';
import {
  CompanyFilterValues,
  FILTER_FIELDS,
  FILTER_FIELD_BY_KEY,
  FilterFieldKey,
  FilterGroup,
  ensureConditionArray,
  SubSector,
} from '@app/model';
import { TranslateService } from '@ngx-translate/core';
import { logger } from '@app/shared';
import { map } from 'rxjs/operators';

type FilterValue = keyof CompanyFilterValues;

/** A select with a search field, such as `app-multi-select`. */
export interface SearchableSelect {
  filterValue: string | null | undefined;
  resetFilter(): void;
  hide(): void;
}

const dateProperties = ['founded_at', 'last_funded_year'];

/** app-date-input needs Date values; filters loaded from a list carry strings. */
const toCalendarValue = (property: string, value: unknown) =>
  dateProperties.includes(property) &&
  (typeof value === 'string' || typeof value === 'number') &&
  value !== ''
    ? new Date(value)
    : value;

/** Where the builder remembers its open groups. */
export const OPEN_GROUPS_KEY = 'hunter.filterBuilder.openGroups';

const readOpenGroups = (): FilterGroup[] | null => {
  try {
    const stored = JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) ?? 'null');
    return Array.isArray(stored) ? stored : null;
  } catch {
    return null;
  }
};

const writeOpenGroups = (groups: FilterGroup[]) => {
  try {
    localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(groups));
  } catch {
    // Without storage the builder opens the first group next time.
  }
};

/** The filter in the form of the API, one condition or conditions joined by and. */
export const joinConditions = (conditions: unknown[]): unknown[] =>
  conditions.length === 1
    ? (conditions[0] as unknown[])
    : conditions.reduce(
        (acc: unknown[], cur, idx) => [
          ...acc,
          ...(idx > 0 ? ['and', cur] : [cur]),
        ],
        [] as unknown[]
      );

const initialSimpleFilterValues: CompanyFilterValues = {
  name: '',
  review_status: '',
  reviewer: '',
  sectors: [],
  last_employee_countFrom: null,
  last_employee_countTo: null,
  investors: '',
  revenueFrom: null,
  revenueTo: null,
  ownership_type: '',
  country_code: [],
  founded_atFrom: '',
  founded_atTo: '',
  last_investment_amountFrom: null,
  last_investment_amountTo: null,
  city: '',
  attractiveness_scoreFrom: null,
  attractiveness_scoreTo: null,
  last_employee_growthFrom: null,
  last_employee_growthTo: null,
  last_funded_yearFrom: '',
  last_funded_yearTo: '',
  last_ebitdaFrom: null,
  last_ebitdaTo: null,
  keywords: [],
};

@Component({
  selector: 'app-companies-filter',
  templateUrl: './companies-filter.component.html',
  styleUrls: ['./companies-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CompaniesFilterComponent implements OnChanges, OnDestroy {
  private companyService = inject(CompanyService);
  private translate = inject(TranslateService);

  /** The groups of the builder, in their order. */
  public readonly groups: { key: FilterGroup; label: string }[] = [
    { key: 'company', label: 'filter.group.company' },
    { key: 'size', label: 'filter.group.size' },
    { key: 'funding', label: 'filter.group.funding' },
    { key: 'review', label: 'filter.group.review' },
  ];
  /** The text of "Search field …", which hides the other fields. */
  public fieldSearch = '';

  /** The groups whose fields are shown; the others show their header only. */
  public openGroups = new Set<FilterGroup>(readOpenGroups() ?? ['company']);

  @Input() filter: unknown[] | null = null;
  @Input() filterUpdateOnChange = false;
  /**
   * Sends a change after this pause in ms, so typing does not send per key;
   * Enter sends at once. 0 sends on Enter only. The pages put the change into
   * the chips of the query bar, which searches on Enter or its Search button.
   */
  @Input() liveDelay = 0;
  @Input() filterEnabled = true;
  @Output() filterChange = new EventEmitter<unknown[] | null>();
  /** Enter in a field: the change is sent at once, then the page searches. */
  @Output() submitted = new EventEmitter<void>();

  private liveTimer: ReturnType<typeof setTimeout> | null = null;
  /** The filter last sent or received, so the same filter is not sent twice. */
  private lastFilter: string | null = null;
  /** A change waits until the builder is enabled again. */
  private changePending = false;

  public simpleFilterValues = { ...initialSimpleFilterValues };
  public simpleFilter: unknown[] = [];

  // Option objects: the search of app-multi-select looks at the labels.
  public countryCodes$ = this.companyService.countryCodes$.pipe(
    map((countries) =>
      countries.map((country) => ({ label: country, value: country }))
    )
  );
  public sectors$ = this.companyService.sectors$.pipe(
    map((sectors) =>
      sectors.reduce((acc, cur) => [...acc, ...cur.items], [] as SubSector[])
    )
  );

  /** The fields of a group whose name or prefix contains the search. */
  shownFields(group: FilterGroup, search: string): FilterFieldKey[] {
    const needle = search.trim().toLowerCase();
    return FILTER_FIELDS.filter(
      (field) =>
        field.group === group &&
        !field.hidden &&
        (!needle ||
          this.translate.instant(field.label).toLowerCase().includes(needle) ||
          field.token.includes(needle))
    ).map((field) => field.key);
  }

  /** The number of conditions of a group. */
  activeCount(group: FilterGroup): number {
    return this.activeFields(group).length;
  }

  /** The labels of the fields of a group that have a condition. */
  activeFields(group: FilterGroup): string[] {
    return this.simpleFilter
      .map((condition) =>
        FILTER_FIELD_BY_KEY.get((condition as [FilterFieldKey])[0])
      )
      .filter((field) => field?.group === group && !field.hidden)
      .map((field) => field?.label as string);
  }

  /** A group is open when the user opened it or the field search is used. */
  isOpen(group: FilterGroup, search: string): boolean {
    return !!search.trim() || this.openGroups.has(group);
  }

  toggleGroup(group: FilterGroup) {
    const open = new Set(this.openGroups);
    if (open.has(group)) {
      open.delete(group);
    } else {
      open.add(group);
    }
    this.openGroups = open;
    writeOpenGroups([...open]);
  }

  ngOnDestroy(): void {
    this.cancelLiveUpdate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['filterEnabled'] &&
      this.filterEnabled &&
      this.changePending &&
      !changes['filter']
    ) {
      this.updateFilter();
    }
    if (changes['filter']) {
      const incoming = JSON.stringify(
        this.filter === null ? [] : ensureConditionArray(this.filter)
      );
      // The filter this builder sent comes back through the URL; the form
      // keeps what was typed since.
      if (incoming === this.lastFilter) {
        return;
      }
      this.lastFilter = incoming;
      this.cancelLiveUpdate();
      this.changePending = false;
      logger.info('companies-filter.ngOnChanges start', {
        filter: this.filter,
        simpleFilter: this.simpleFilter,
        filterValues: this.simpleFilterValues,
      });
      // The form shows the given filter only. Fields it does not name are
      // emptied, so a filter from the URL (browser back) or of another list
      // does not keep the values of the one before.
      this.simpleFilterValues = {
        ...initialSimpleFilterValues,
        sectors: [],
        country_code: [],
        keywords: [],
      };
      this.simpleFilter =
        this.filter === null ? [] : ensureConditionArray(this.filter);

      this.simpleFilter.forEach((filter) => {
        const [property, operator, value] = filter as [string, string, unknown];

        if (property === 'keywords') {
          if (
            typeof value === 'string' &&
            this.simpleFilterValues.keywords.join('&') !== value
          ) {
            this.simpleFilterValues.keywords = value.split('&');
          } else if (
            typeof value !== 'string' &&
            this.simpleFilterValues.keywords.length
          ) {
            this.simpleFilterValues.keywords =
              initialSimpleFilterValues.keywords;
          }
        } else if (
          [
            'last_employee_count',
            'revenue',
            'founded_at',
            'last_investment_amount',
            'attractiveness_score',
            'last_employee_growth',
            'last_funded_year',
            'last_ebitda',
          ].includes(property)
        ) {
          if (operator === 'between') {
            this.simpleFilterValues[(property + 'From') as FilterValue] =
              toCalendarValue(property, (value as unknown[])[0]) as never;
            this.simpleFilterValues[(property + 'To') as FilterValue] =
              toCalendarValue(property, (value as unknown[])[1]) as never;
          } else {
            this.simpleFilterValues[
              (property + (operator === '>=' ? 'From' : 'To')) as FilterValue
            ] = toCalendarValue(property, value) as never;
          }
        } else {
          this.simpleFilterValues[property as FilterValue] = (
            (property === 'sectors' || property === 'country_code') &&
            !(value instanceof Array)
              ? [value]
              : value
          ) as never;
        }
      });

      if (changes['filter'].firstChange) {
        // Groups with conditions open as well, so the values are in view.
        const open = new Set(this.openGroups);
        this.groups
          .filter((group) => this.activeCount(group.key))
          .forEach((group) => open.add(group.key));
        this.openGroups = open;
      }

      logger.info('companies-filter.ngOnChanges done', {
        filter: this.filter,
        simpleFilter: this.simpleFilter,
        filterValues: this.simpleFilterValues,
      });
    }
  }

  updateFilter() {
    logger.info('companies-filter.updateFilter');
    this.cancelLiveUpdate();
    if (!this.filterEnabled) {
      this.changePending = true;
      return;
    }
    this.changePending = false;
    const serialized = JSON.stringify(this.simpleFilter);
    if (serialized === this.lastFilter) {
      return;
    }
    this.lastFilter = serialized;
    this.filterChange.emit(joinConditions(this.simpleFilter));
  }

  enterPressed() {
    logger.info('enterPressed -> updateFilter');
    if (!this.filterUpdateOnChange) {
      this.updateFilter();
    }
    this.submitted.emit();
  }

  private scheduleLiveUpdate() {
    this.cancelLiveUpdate();
    this.liveTimer = setTimeout(() => this.updateFilter(), this.liveDelay);
  }

  private cancelLiveUpdate() {
    if (this.liveTimer !== null) {
      clearTimeout(this.liveTimer);
      this.liveTimer = null;
    }
  }

  /** Empties a text filter with its clear button. */
  clearTextFilter(property: 'name' | 'investors' | 'city' | 'reviewer') {
    this.simpleFilterValues[property] = '';
    this.simpleFilterChanged(property, 'contains');
  }

  /** Removes a tag of a multi select with its remove button. */
  removeTag(
    event: Event,
    property: 'sectors' | 'country_code',
    operator: string,
    value: unknown
  ) {
    event.stopPropagation();
    this.simpleFilterValues = {
      ...this.simpleFilterValues,
      [property]: (this.simpleFilterValues[property] as unknown[]).filter(
        (x) => x !== value
      ),
    };
    this.simpleFilterChanged(property, operator);
  }

  sectorName(sectors: SubSector[], id: number) {
    return sectors.find((sector) => sector.id === id)?.name ?? id;
  }

  simpleFilterChanged(
    property: string,
    operator: string,
    select?: SearchableSelect
  ) {
    logger.info(
      'companies-filter.simpleFilterChanged',
      { property, operator },
      this.filter,
      this.simpleFilter
    );
    const sanitizeValue = (input: string | number | Date, startOf = false) => {
      if (['last_funded_year', 'founded_at'].includes(property)) {
        const date = new Date(input as string | number | Date);
        if (property === 'last_funded_year') {
          if (startOf) {
            date.setDate(1);
            date.setMonth(0);
          } else {
            date.setDate(31);
            date.setMonth(11);
          }
        }

        return date;
      } else {
        return +(input as string | number);
      }
    };

    let filterExpression: null | unknown[] = null;
    if (operator === 'between') {
      const filterFrom = this.simpleFilterValues[
        (property + 'From') as FilterValue
      ] as string | number | Date | null;
      const filterTo = this.simpleFilterValues[
        (property + 'To') as FilterValue
      ] as string | number | Date | null;
      if (filterFrom && filterTo) {
        filterExpression = [
          property,
          operator,
          [sanitizeValue(filterFrom, true), sanitizeValue(filterTo, false)],
        ];
      } else if (filterFrom && !filterTo) {
        filterExpression = [property, '>=', sanitizeValue(filterFrom, true)];
      } else if (!filterFrom && filterTo) {
        filterExpression = [property, '<=', sanitizeValue(filterTo, false)];
      } else {
        filterExpression = null;
      }
    } else if (property === 'keywords') {
      if (this.simpleFilterValues.keywords.length) {
        filterExpression = [
          property,
          'contains',
          this.simpleFilterValues.keywords.join('&'),
        ];
      }
    } else if (
      (property !== 'country_code' && property !== 'sectors') ||
      (property === 'country_code' &&
        this.simpleFilterValues.country_code.length) ||
      (property === 'sectors' && this.simpleFilterValues.sectors.length)
    ) {
      const filterValue = this.simpleFilterValues[property as FilterValue];
      filterExpression = filterValue ? [property, operator, filterValue] : null;
    }

    // Like a tag box: picking an entry from a search result clears the search
    // text and closes the list.
    if (select && select.filterValue) {
      select.resetFilter();
      select.hide();
    }

    this.simpleFilter = this.simpleFilter.reduce(
      (acc: unknown[], cur) =>
        cur instanceof Array && cur[0] === property ? acc : [...acc, cur],
      [] as unknown[]
    );

    if (filterExpression !== null) {
      this.simpleFilter = [...this.simpleFilter, filterExpression];
    }
    if (this.filterUpdateOnChange) {
      this.updateFilter();
    } else if (this.liveDelay > 0) {
      this.scheduleLiveUpdate();
    }
  }
}
